import React, { useMemo } from "react";
import blog from "./BlogJSON/GitJSON.json";
import BlogSideBarRight from "./BlogSideBarRight";
import ScrollButton from "../../ScrollButton";
import ProfilePic from "../../images/profile/profile-pic.jpeg";
import { Link } from "react-router-dom";

/**
 * LambdaStepFunctionBlog — Professional blog layout
 * - Hero header with metadata and share
 * - Left sticky Author card + On This Page
 * - Clean readable typography
 * - MCQ section rendered inline (no collapses)
 * - Commands section with copy buttons
 * - Right sidebar preserved
 */

// naive reading time (~200 wpm)
const readingTime = (text) => Math.max(2, Math.round(text.split(/\s+/).length / 200));

const LambdaStepFunctionBlog = () => {
  const allText = useMemo(() => {
    const mcqText = (blog.mcqs || [])
      .map((q) => `${q.question} ${q?.answer?.brief ?? ""} ${(q?.answer?.bullets ?? []).join(" ")}`)
      .join(" ");
    const cmdText = (blog.gitCommands?.command || [])
      .map((c) => `${c.name} ${c.description}`)
      .join(" ");
    return `${blog.title} ${blog.introduction} ${mcqText} ${blog.gitCommands?.intro ?? ""} ${cmdText} ${blog.conclusion ?? ""}`;
  }, []);

  const minutes = readingTime(allText);

  const share = (network) => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(blog.title);
    const via = "TechVentureLLC";
    const map = {
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}&via=${via}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    };
    const win = window.open(map[network], "_blank", "noopener,noreferrer");
    if (win) win.focus();
  };

  const copy = async (cmd) => {
    try {
      await navigator.clipboard.writeText(cmd);
      // tiny toast substitute
      alert(`Copied: ${cmd}`);
    } catch {}
  };

  // ---- Small UI helpers -------------------------------------------------------
const H1 = ({ children, id }) => (
  <h1 id={id} className="text-3xl md:text-4xl font-extrabold tracking-tight mb-4">
    {children}
  </h1>
);

const H2 = ({ children, id }) => (
  <h2 id={id} className="text-2xl md:text-3xl font-bold tracking-tight mt-10 mb-3">
    {children}
  </h2>
);

const H3 = ({ children, id }) => (
  <h3 id={id} className="text-xl md:text-2xl font-semibold mt-8 mb-2">
    {children}
  </h3>
);

const P = ({ children }) => (
  <p className="leading-7 text-slate-700 dark:text-slate-300 mb-4">{children}</p>
);

const Li = ({ children }) => (
  <li className="mb-1 leading-7 text-slate-700 dark:text-slate-300">{children}</li>
);

const Code = ({ children }) => (
  <code className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-mono text-[0.95em]">
    {children}
  </code>
);

const Pre = ({ children, title }) => (
  <div className="my-4 border rounded-xl overflow-hidden shadow-sm bg-white dark:bg-slate-900">
    {title && (
      <div className="px-4 py-2 text-xs uppercase tracking-wide bg-slate-50 dark:bg-slate-800 text-slate-500 border-b">
        {title}
      </div>
    )}
    <pre className="p-4 overflow-auto text-sm leading-6 bg-slate-50/60 dark:bg-slate-900 text-slate-800 dark:text-slate-200">
      <code>{children}</code>
    </pre>
  </div>
);

const Callout = ({ tone = "info", title, children }) => {
  const tones = {
    info: {
      ring: "ring-sky-200 dark:ring-sky-900/60",
      bg: "bg-sky-50 dark:bg-sky-900/40",
      text: "text-sky-900 dark:text-sky-100",
      badge: "bg-sky-600",
    },
    warn: {
      ring: "ring-amber-200 dark:ring-amber-900/60",
      bg: "bg-amber-50 dark:bg-amber-900/40",
      text: "text-amber-900 dark:text-amber-100",
      badge: "bg-amber-600",
    },
    danger: {
      ring: "ring-rose-200 dark:ring-rose-900/60",
      bg: "bg-rose-50 dark:bg-rose-900/40",
      text: "text-rose-900 dark:text-rose-100",
      badge: "bg-rose-600",
    },
    success: {
      ring: "ring-emerald-200 dark:ring-emerald-900/60",
      bg: "bg-emerald-50 dark:bg-emerald-900/40",
      text: "text-emerald-900 dark:text-emerald-100",
      badge: "bg-emerald-600",
    },
  };
  const t = tones[tone] ?? tones.info;
  return (
    <div className={`my-4 rounded-xl ${t.bg} ${t.ring} ring-1 p-4` }>
      {title && (
        <div className="text-xs font-semibold uppercase tracking-wide mb-2 text-white inline-flex px-2 py-0.5 rounded-full" style={{ backgroundColor: undefined }}>
          <span className={`${t.badge} px-2 py-0.5 rounded-full`}>{title}</span>
        </div>
      )}
      <div className={`${t.text}`}>{children}</div>
    </div>
  );
};

const Kbd = ({ children }) => (
  <kbd className="px-1.5 py-0.5 rounded border bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 font-mono text-xs">
    {children}
  </kbd>
);


  return (
    <div className="container-fluid">
      <div className="row g-4 flex-xl-nowrap">
        {/* LEFT: Author */}
        <aside className="d-none d-xl-block col-xl-2 pt-3">
          <div className="position-sticky" style={{ top: "1rem" }}>
            {/* Author */}
            <div className="card shadow-sm border-0 mb-3 text-center">
              <div className="card-body">
                <img src={ProfilePic} className="rounded-circle mb-2" alt="Author" style={{ width: 80, height: 80, objectFit: "cover" }} />
                <h3 className="h6 mb-1">Rabin Pantha</h3>
                <p className="small text-muted mb-2">Sr. Software Engineer, Walgreens</p>
                <a className="btn btn-outline-primary btn-sm" href="https://www.linkedin.com/in/rabinpantha" target="_blank" rel="noopener noreferrer">LinkedIn</a>
              </div>
            </div>

            {/* On This Page */}
           {/* On This Page */}
<div className="card shadow-sm border-0">
  <div className="card-body">
    <h2 className="h6 text-uppercase text-muted mb-2">On this page</h2>
    <nav className="nav flex-column small">
      <a className="nav-link px-0" href="#overview">Overview</a>
      <a className="nav-link px-0" href="#why">Why Lambda + Step Functions</a>
      <a className="nav-link px-0" href="#arch">Reference Architecture</a>

      <a className="nav-link px-0" href="#code">Mini Project (All-Python)</a>
      <div className="ms-3">
        <a className="nav-link px-0" href="#code-cdk">• CDK Stack (Python)</a>
        <a className="nav-link px-0" href="#code-lambda-a">• Lambda A (Invoker)</a>
        <a className="nav-link px-0" href="#code-lambda-b">• Lambda B (Worker)</a>
      </div>

      <a className="nav-link px-0" href="#usecases">Common Use Cases</a>
      <a className="nav-link px-0" href="#benefits">Benefits</a>
      <a className="nav-link px-0" href="#drawbacks">Drawbacks / Pitfalls</a>
      <a className="nav-link px-0" href="#consider">Things to Consider</a>

      <a className="nav-link px-0" href="#best">Best Practices & Cost</a>
      <div className="ms-3">
        <a className="nav-link px-0" href="#best-lambda">• Lambda</a>
        <a className="nav-link px-0" href="#best-sfn">• Step Functions</a>
      </div>

      <a className="nav-link px-0" href="#ops">Observability, Testing, Security</a>
      <a className="nav-link px-0" href="#deploy">Deploy & Run</a>

      <a className="nav-link px-0" href="#sfn-components">Step Functions Components</a>
      <a className="nav-link px-0" href="#glossary">Glossary</a>
    </nav>
  </div>
</div>

          </div>
        </aside>

        {/* MAIN */}
        <main className="col-12 col-lg-8 col-xl-7 pt-3" role="main">
          {/* Hero */}
          <div className="min-h-screen w-full bg-gradient-to-b from-white to-slate-50 dark:from-slate-950 dark:to-slate-900">
      <div className="mx-auto max-w-6xl px-4 md:px-6 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-8">
          <article className="prose prose-slate dark:prose-invert max-w-none">
            <header className="mb-8">
              <H1 id="overview">AWS Lambda + Step Functions</H1>
              <P>
                This page consolidates a hands-on architecture using <Code>AWS Lambda</Code> and <Code>AWS Step Functions</Code>, plus a deep dive into benefits, real-world use cases, cost and performance tips, and pitfalls to avoid. It includes a complete, copy-ready project using <Code>CDK (Python)</Code> and two <Code>Python</Code> Lambdas orchestrated by an <Code>Express</Code> state machine.
              </P>
            </header>

            <H2 id="why">Why Lambda + Step Functions</H2>
            <P>
              Pairing Lambda with Step Functions gives you managed, serverless compute and a visual workflow engine with retries, branching, and parallelism. You write minimal glue code, push orchestration into state definitions, and gain robust error handling without rolling your own state machine.
            </P>
            <ul className="list-disc ml-6">
              <Li><strong>Separation of concerns:</strong> Lambdas focus on business logic; Step Functions own orchestration and retries.</Li>
              <Li><strong>Elastic by default:</strong> Scale to thousands of parallel tasks with <Code>Map</Code> and <Code>Distributed Map</Code>.</Li>
              <Li><strong>Pay-per-use:</strong> Cost tracks invocations and duration; no servers to manage.</Li>
              <Li><strong>Built-in reliability:</strong> Declarative <Code>Retry</Code>, <Code>Catch</Code>, <Code>Timeout</Code>, and <Code>Heartbeat</Code>.</Li>
            </ul>

            <H2 id="arch">Reference Architecture</H2>
            <P>
              The minimal pattern: <strong>Lambda A</strong> (Invoker) starts an <strong>Express</strong> state machine using <Code>StartSyncExecution</Code>. The state machine invokes <strong>Lambda B</strong> and returns its result to Lambda A, which then replies to the caller (e.g., API Gateway).
            </P>
            <Pre title="High-level data flow">
{`Client/API → Lambda A → StartSyncExecution → Step Functions (Express)
                               │
                               └─ LambdaInvoke → Lambda B → result → Step Functions → Lambda A → response`}
            </Pre>
            <Callout tone="info" title="Tip">
              For long-running or human-in-the-loop workflows, consider <Code>Standard</Code> Step Functions and asynchronous patterns (<Code>StartExecution</Code> + callbacks / task tokens).
            </Callout>

            <H2 id="code">Mini Project (All-Python)</H2>
            <P>
              Copy these files into a folder (e.g., <Code>lambda-sfn-python/</Code>). Run CDK to deploy. The stack creates two Lambdas (Invoker / Worker) and an Express state machine that synchronously returns the worker result.
            </P>

            <H3 id="code-cdk">CDK Stack (Python)</H3>
            <Pre title="lambda_sfn_python_stack.py">
{`from constructs import Construct
import aws_cdk as cdk
from aws_cdk import (
    Duration,
    aws_lambda as _lambda,
    aws_stepfunctions as sfn,
    aws_stepfunctions_tasks as tasks,
)
from pathlib import Path

LAMBDA_DIR = str(Path(__file__).parent.joinpath("lambda").resolve())

class LambdaSfnPythonStack(cdk.Stack):
    def __init__(self, scope: Construct, construct_id: str, **kwargs) -> None:
        super().__init__(scope, construct_id, **kwargs)

        worker_fn = _lambda.Function(
            self, "WorkerFn",
            runtime=_lambda.Runtime.PYTHON_3_12,
            handler="lambda_b.handler",
            code=_lambda.Code.from_asset(LAMBDA_DIR),
            memory_size=256,
            timeout=Duration.seconds(10),
        )

        invoke_worker = tasks.LambdaInvoke(
            self, "InvokeWorker",
            lambda_function=worker_fn,
            payload_response_only=True,
        )

        state_machine = sfn.StateMachine(
            self, "DemoExpressStateMachine",
            definition_body=sfn.DefinitionBody.from_chainable(invoke_worker),
            state_machine_type=sfn.StateMachineType.EXPRESS,
            tracing_enabled=True,
        )

        invoker_fn = _lambda.Function(
            self, "InvokerFn",
            runtime=_lambda.Runtime.PYTHON_3_12,
            handler="lambda_a.handler",
            code=_lambda.Code.from_asset(LAMBDA_DIR),
            memory_size=256,
            timeout=Duration.seconds(10),
            environment={"STATE_MACHINE_ARN": state_machine.state_machine_arn},
        )

        state_machine.grant_start_sync_execution(invoker_fn)
`}
            </Pre>

            <H3 id="code-lambda-a">Lambda A (Invoker)</H3>
            <Pre title="lambda/lambda_a.py">
{`import json, os, uuid
from datetime import datetime
import boto3

SFN_ARN = os.environ["STATE_MACHINE_ARN"]
sfn = boto3.client("stepfunctions")

def handler(event, context):
    payload = {
        "correlationId": str(uuid.uuid4()),
        "receivedAt": datetime.utcnow().isoformat() + "Z",
        "request": event,
    }
    resp = sfn.start_sync_execution(stateMachineArn=SFN_ARN, input=json.dumps(payload))
    out = resp.get("output")
    return {
        "ok": resp.get("status") == "SUCCEEDED",
        "result": json.loads(out) if out else None,
    }
`}
            </Pre>

            <H3 id="code-lambda-b">Lambda B (Worker)</H3>
            <Pre title="lambda/lambda_b.py">
{`def handler(event, context):
    req = (event or {}).get("request", {})
    op = req.get("operation", "echo")
    n = req.get("number", 0)
    if op == "double":
        val, msg = n * 2, f"doubled {n} -> {n*2}"
    elif op == "square":
        val, msg = n * n, f"squared {n} -> {n*n}"
    else:
        val, msg = req, "echo"
    return {"operation": op, "inputNumber": n, "value": val, "message": msg}
`}
            </Pre>

            <H2 id="usecases">Common Use Cases</H2>
            <ul className="list-disc ml-6">
              <Li><strong>Event-driven ETL:</strong> Ingest → transform → validate → load, with fan-out via <Code>Map</Code>.</Li>
              <Li><strong>API composition:</strong> Aggregate multiple downstream calls with retries and timeouts.</Li>
              <Li><strong>Human-in-the-loop:</strong> Approval steps, wait states, and callbacks using task tokens (Standard).</Li>
              <Li><strong>ML pipelines:</strong> Pre/post-processing around SageMaker jobs or Batch tasks.</Li>
              <Li><strong>Back-office ops:</strong> Scheduled workflows, report generation, batch reconciliation.</Li>
            </ul>

            <H2 id="benefits">Benefits</H2>
            <ul className="list-disc ml-6">
              <Li><strong>Fast iteration:</strong> Small, focused functions and declarative state machines.</Li>
              <Li><strong>Resilience built-in:</strong> <Code>Retry</Code>/<Code>Catch</Code>/backoff without custom frameworks.</Li>
              <Li><strong>Parallelism:</strong> <Code>Map</Code> and <Code>Distributed Map</Code> scale out work safely.</Li>
              <Li><strong>Observability:</strong> Execution history + CloudWatch Logs + X-Ray traces.</Li>
            </ul>

            <H2 id="drawbacks">Drawbacks / Pitfalls</H2>
            <ul className="list-disc ml-6">
              <Li><strong>State history cost & size:</strong> Large payloads increase cost and may hit limits. Store blobs in S3 and pass references.</Li>
              <Li><strong>Cold starts / latency:</strong> Latency-sensitive APIs may require Provisioned Concurrency or lighter runtimes.</Li>
              <Li><strong>VPC overhead:</strong> Only attach a VPC when needed (private DBs). Consider VPC endpoints to avoid NAT costs.</Li>
              <Li><strong>Long-running work:</strong> Prefer Standard type or move heavy compute to ECS/EKS/Batch with callbacks.</Li>
              <Li><strong>Fan-out pressure:</strong> Cap concurrency to protect downstreams and add DLQs.</Li>
            </ul>

            <H2 id="consider">Things to Consider</H2>
            <ul className="list-disc ml-6">
              <Li><strong>Limits:</strong> Payload sizes, history retention, execution duration differ between Express and Standard.</Li>
              <Li><strong>Idempotency:</strong> Retries happen—design safe, idempotent handlers.</Li>
              <Li><strong>Partial batch success:</strong> For SQS/Kinesis sources, enable partial responses to avoid reprocessing successes.</Li>
              <Li><strong>Schema & contracts:</strong> Version your inputs/outputs; validate early.</Li>
              <Li><strong>Security:</strong> Least-privilege IAM, parameterize secrets with Secrets Manager/SSM.
              </Li>
            </ul>

            <H2 id="best">Best Practices & Cost</H2>
            <H3 id="best-lambda">Lambda</H3>
            <ul className="list-disc ml-6">
              <Li>Right-size memory (CPU scales with memory). Benchmark a few points for fastest-cheapest mix.</Li>
              <Li>Trim dependencies; lazy-init heavy SDKs. Cache clients outside handlers.</Li>
              <Li>Use Provisioned Concurrency or SnapStart (runtime support varies) for consistent latency.</Li>
              <Li>Increase <Code>/tmp</Code> only when needed (512MB→10GB).</Li>
            </ul>
            <H3 id="best-sfn">Step Functions</H3>
            <ul className="list-disc ml-6">
              <Li>Prefer service integrations (DynamoDB, S3, EventBridge, ECS) over Lambda glue.</Li>
              <Li>Control payload growth with <Code>ResultPath</Code>, <Code>Parameters</Code>, and <Code>ResultSelector</Code>.</Li>
              <Li>Use <Code>Map</Code>/<Code>Distributed Map</Code> for parallelism; cap concurrency.</Li>
              <Li>Pick <strong>Express</strong> for high-throughput short tasks; <strong>Standard</strong> for long-lived workflows.</Li>
            </ul>

            <H2 id="ops">Observability, Testing, Security</H2>
            <ul className="list-disc ml-6">
              <Li><strong>Logging/Tracing:</strong> Structured logs, X-Ray traces across Lambda and Step Functions.</Li>
              <Li><strong>Metrics/SLOs:</strong> Emit p95 latency, error rate, cost per unit, DLQ depth. Alarm on anomalies.</Li>
              <Li><strong>Testing:</strong> Unit-test handlers; integration-test state definitions via LocalStack or Step Functions local.</Li>
              <Li><strong>Security:</strong> Fine-grained IAM; encrypt at rest (KMS) and in-transit (TLS). Rotate secrets.</Li>
            </ul>

            <H2 id="deploy">Deploy & Run</H2>
            <Pre title="requirements.txt">
{`aws-cdk-lib==2.154.0
constructs>=10.0.0,<11.0.0`}
            </Pre>
            <Pre title="app.py">
{`import aws_cdk as cdk
from lambda_sfn_python_stack import LambdaSfnPythonStack
app = cdk.App()
LambdaSfnPythonStack(app, "LambdaSfnPythonStack")
app.synth()`}
            </Pre>
            <P>
              Bootstrap once per account/region with <Kbd>cdk bootstrap</Kbd>, then <Kbd>cdk deploy</Kbd>. Invoke the invoker Lambda with a payload like:
            </P>
            <Pre title="Invoke example">
{`aws lambda invoke \
  --function-name <InvokerFnName> \
  --payload '{"operation":"double","number":21}' \
  --cli-binary-format raw-in-base64-out \
  response.json && cat response.json`}
            </Pre>

            {/* ------------------------------------------------------------------ */}
{/* Step Functions Components                                          */}
{/* ------------------------------------------------------------------ */}
<H2 id="sfn-components">AWS Step Functions Components</H2>
<P>
  AWS Step Functions orchestrates workflows with a declarative JSON spec called
  <Code>Amazon States Language (ASL)</Code>. Below are the core building blocks you’ll use to
  design, run, and operate your workflows.
</P>

<H3>State Machine</H3>
<P>
  The top-level workflow definition written in ASL. It declares the <Code>StartAt</Code> state,
  the full set of <Code>States</Code>, and (optionally) <Code>TimeoutSeconds</Code>, <Code>Tracing</Code>, etc.
</P>
<Pre title="Minimal ASL skeleton">
{`{
  "Comment": "Example workflow",
  "StartAt": "DoWork",
  "States": {
    "DoWork": { "Type": "Pass", "Result": "ok", "End": true }
  }
}`}
</Pre>

<H3>States</H3>
<P>
  States are the steps in your workflow. Common types:
</P>

<H3 id="task-state">Task State</H3>
<P>
  Performs work by calling an AWS service (service integration) or an external worker (Activity).
  Use <Code>Retry</Code>/<Code>Catch</Code> for resilience, and <Code>TimeoutSeconds</Code>/<Code>HeartbeatSeconds</Code> to detect hangs.
</P>
<Pre title="Task → Invoke Lambda (service integration)">
{`{
  "Type": "Task",
  "Resource": "arn:aws:states:::lambda:invoke",
  "Parameters": {
    "FunctionName": "arn:aws:lambda:REGION:ACCOUNT:function:WorkerFn",
    "Payload.$": "$"              // pass whole input; use ResultSelector/Path to trim
  },
  "ResultPath": "$.worker",
  "Retry": [{ "ErrorEquals": ["States.ALL"], "IntervalSeconds": 2, "MaxAttempts": 3, "BackoffRate": 2.0 }],
  "Catch":  [{ "ErrorEquals": ["States.ALL"], "ResultPath": "$.error", "Next": "FailState" }],
  "Next": "NextState"
}`}
</Pre>

<H3 id="choice-state">Choice State</H3>
<P>
  Adds branching logic using JSONPath conditions.
</P>
<Pre title="Choice branching">
{`{
  "Type": "Choice",
  "Choices": [
    { "Variable": "$.request.operation", "StringEquals": "double", "Next": "Double" },
    { "Variable": "$.request.operation", "StringEquals": "square", "Next": "Square" }
  ],
  "Default": "Echo"
}`}
</Pre>

<H3 id="parallel-state">Parallel State</H3>
<P>
  Runs multiple branches concurrently. Execution continues when all (or the first, if configured) branches complete.
</P>
<Pre title="Parallel branches">
{`{
  "Type": "Parallel",
  "Branches": [
    { "StartAt": "A1", "States": { "A1": { "Type": "Pass", "Result": "A", "End": true } } },
    { "StartAt": "B1", "States": { "B1": { "Type": "Pass", "Result": "B", "End": true } } }
  ],
  "ResultPath": "$.parallel",
  "Next": "NextState"
}`}
</Pre>

<H3 id="map-state">Map State</H3>
<P>
  Iterates over an array. Two modes:
  <strong> Inline Map</strong> (bounded fan-out) and
  <strong> Distributed Map</strong> (massive scale; items can come from S3).
  Use <Code>MaxConcurrency</Code> to protect downstreams.
</P>
<Pre title="Inline Map over $.items">
{`{
  "Type": "Map",
  "ItemsPath": "$.items",
  "MaxConcurrency": 40,
  "Iterator": {
    "StartAt": "ProcessItem",
    "States": {
      "ProcessItem": {
        "Type": "Task",
        "Resource": "arn:aws:states:::lambda:invoke",
        "Parameters": { "FunctionName": "WorkerFn", "Payload.$": "$" },
        "End": true
      }
    }
  },
  "ResultPath": "$.processed",
  "Next": "NextState"
}`}
</Pre>

<H3 id="pass-state">Pass State</H3>
<P>Passes input to output without work. Useful for shaping data.</P>
<Pre title="Pass (shape data)">
{`{ "Type": "Pass", "Parameters": { "status": "ok", "input.$": "$" }, "ResultPath": "$.meta", "Next": "NextState" }`}
</Pre>

<H3 id="wait-state">Wait State</H3>
<P>Pauses execution by duration or until a specific timestamp.</P>
<Pre title="Wait">
{`{ "Type": "Wait", "Seconds": 30, "Next": "NextState" }`}
</Pre>

<H3 id="succeed-fail">Succeed / Fail State</H3>
<P>Explicitly end an execution successfully or as a failure.</P>
<Pre title="Succeed & Fail">
{`{ "Type": "Succeed" }   // or   { "Type": "Fail", "Error": "MyError", "Cause": "Message" }`}
</Pre>

<H3 id="activities">Activity Tasks (external workers)</H3>
<P>
  For work performed by non-AWS systems that poll Step Functions. Prefer service integrations or Lambda when possible.
</P>
<Pre title="Activity task">
{`{ "Type": "Task", "Resource": "arn:aws:states:REGION:ACCOUNT:activity:MyActivity", "Next": "NextState" }`}
</Pre>

<H3 id="executions">Executions</H3>
<P>
  Each run of a state machine is an <strong>Execution</strong> with its own input, history, and output.
  Express supports <Code>StartSyncExecution</Code> (returns output inline). Standard supports long-running workflows and rich history.
</P>

<H3 id="asl">Amazon States Language (ASL)</H3>
<P>
  JSON language for declaring states, transitions, and error handling.
  Key fields: <Code>Type</Code>, <Code>Next</Code>/<Code>End</Code>, <Code>Parameters</Code>, <Code>ResultPath</Code>,
  <Code>ResultSelector</Code>, <Code>Retry</Code>, <Code>Catch</Code>, <Code>TimeoutSeconds</Code>, <Code>HeartbeatSeconds</Code>.
</P>
<Pre title="Putting it together (mini workflow)">
{`{
  "Comment": "Lambda A -> Step Functions -> Lambda B",
  "StartAt": "Validate",
  "States": {
    "Validate": {
      "Type": "Choice",
      "Choices": [
        { "Variable": "$.request.operation", "StringEquals": "double", "Next": "Work" },
        { "Variable": "$.request.operation", "StringEquals": "square", "Next": "Work" }
      ],
      "Default": "Echo"
    },
    "Work": {
      "Type": "Task",
      "Resource": "arn:aws:states:::lambda:invoke",
      "Parameters": { "FunctionName": "WorkerFn", "Payload.$": "$" },
      "ResultPath": "$.result",
      "Retry": [{ "ErrorEquals": ["States.ALL"], "IntervalSeconds": 1, "MaxAttempts": 3, "BackoffRate": 2 }],
      "Catch":  [{ "ErrorEquals": ["States.ALL"], "ResultPath": "$.error", "Next": "Failed" }],
      "Next": "Success"
    },
    "Echo":   { "Type": "Pass", "ResultPath": "$.result", "Next": "Success" },
    "Success":{ "Type": "Succeed" },
    "Failed": { "Type": "Fail", "Error": "Unhandled", "Cause": "See $.error" }
  }
}`}
</Pre>

<H3 id="integrations">AWS Service Integrations</H3>
<P>
  Direct integrations (no Lambda needed) reduce latency and cost: DynamoDB, S3, EventBridge, Step Functions, SageMaker, ECS, Batch,
  Glue, SNS/SQS, Bedrock, and more. Prefer these over Lambda “glue” when possible.
</P>


            <H2 id="glossary">Glossary</H2>
<P>
  Below is a consolidated glossary of AWS Lambda and Step Functions concepts — followed by
  a visual summary table for quick scanning. This helps engineers and architects recall
  each key component, its purpose, and how it fits into real workflows.
</P>

{/* --- Expanded List Version --- */}
<ul className="list-disc ml-6">
  <Li><strong>Amazon States Language (ASL):</strong> The JSON-based language used to define Step Functions workflows, specifying states, transitions, input/output paths, retries, and error handling.</Li>
  <Li><strong>State Machine:</strong> The complete definition of a workflow in Step Functions, made up of individual states and transitions.</Li>
  <Li><strong>State:</strong> A single step in a workflow, such as a task, choice, wait, or success state.</Li>
  <Li><strong>Task State:</strong> Executes a specific unit of work, like invoking a Lambda function or an AWS service integration.</Li>
  <Li><strong>Choice State:</strong> Adds branching logic, directing the workflow based on evaluated conditions.</Li>
  <Li><strong>Parallel State:</strong> Runs multiple branches of execution concurrently, merging results afterward.</Li>
  <Li><strong>Map / Distributed Map:</strong> Iterates over an array or S3 dataset to run sub-state machines for each item.</Li>
  <Li><strong>Pass State:</strong> Passes its input directly to output — often for restructuring or debugging workflows.</Li>
  <Li><strong>Wait State:</strong> Pauses execution until a given time or for a specific duration.</Li>
  <Li><strong>Succeed / Fail State:</strong> Explicitly ends an execution successfully or marks it as failed.</Li>
  <Li><strong>Retry Policy / Catch:</strong> Defines how to handle transient failures or reroute on specific errors.</Li>
  <Li><strong>Parameters / ResultPath / ResultSelector:</strong> Control how data flows between states, reducing payload size.</Li>
  <Li><strong>StartExecution / StartSyncExecution:</strong> APIs to start asynchronous or synchronous executions, respectively.</Li>
  <Li><strong>Task Token:</strong> A callback token allowing external systems or humans to resume paused workflows.</Li>
  <Li><strong>Activity Task:</strong> Represents external work units performed by non-AWS workers polling Step Functions.</Li>
  <Li><strong>Execution ARN:</strong> A globally unique identifier for each execution run.</Li>
  <Li><strong>Execution History:</strong> Timeline log of every state transition and result within a workflow.</Li>
  <Li><strong>Integration Patterns:</strong> How Step Functions communicate with AWS services — Request/Response, Wait-for-Callback, or Fire-and-Forget.</Li>
  <Li><strong>Express Workflow:</strong> Optimized for short-lived, high-throughput workflows with millisecond billing.</Li>
  <Li><strong>Standard Workflow:</strong> Long-running, durable workflows supporting detailed history and manual steps.</Li>
  <Li><strong>IAM Role for Step Functions:</strong> Grants Step Functions permission to invoke services or Lambdas.</Li>
  <Li><strong>Context Object ($$):</strong> Provides metadata like execution name, state name, timestamps, and retry counts.</Li>
  <Li><strong>Dynamic References:</strong> Injects runtime values (e.g., Secrets Manager parameters) into state definitions.</Li>
  <Li><strong>CloudWatch Logs & Metrics:</strong> Default observability layer for Step Functions and Lambda executions.</Li>
  <Li><strong>X-Ray Tracing:</strong> Enables distributed tracing to visualize service dependencies and latency.</Li>
  <Li><strong>DLQ (Dead Letter Queue):</strong> Captures failed messages or executions for later analysis.</Li>
  <Li><strong>Idempotency:</strong> Designing states to handle retries without duplicating side effects.</Li>
  <Li><strong>Execution Output:</strong> The final JSON returned by a completed execution.</Li>
  <Li><strong>Workflow Chaining:</strong> Linking multiple state machines for modular orchestration.</Li>
  <Li><strong>Callback Pattern:</strong> A workflow pattern where Step Functions waits for an external process to respond.</Li>
  <Li><strong>Human Approval Step:</strong> A manual checkpoint awaiting a user decision before continuing.</Li>
  <Li><strong>Cost Model:</strong> Step Functions cost depends on state transitions (Standard) or duration (Express).</Li>
  <Li><strong>Provisioned Concurrency:</strong> Keeps Lambda warm for low-latency requests.</Li>
  <Li><strong>State Transition:</strong> Each move from one state to another — a key billing metric for Standard workflows.</Li>
  <Li><strong>Audit & Compliance:</strong> Execution logs provide tamper-proof evidence trails for governance or security reviews.</Li>
</ul>

{/* --- Visual Summary Table Version --- */}
<div className="mt-8 overflow-x-auto">
  <table className="min-w-full text-left border-collapse border border-slate-200 dark:border-slate-700">
    <thead className="bg-slate-100 dark:bg-slate-800">
      <tr>
        <th className="px-4 py-2 text-sm font-semibold text-slate-700 dark:text-slate-200">Term</th>
        <th className="px-4 py-2 text-sm font-semibold text-slate-700 dark:text-slate-200">Description</th>
      </tr>
    </thead>
    <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
      <tr><td className="px-4 py-2"><Code>ASL</Code></td><td className="px-4 py-2">Amazon States Language — JSON syntax used to define workflows.</td></tr>
      <tr><td className="px-4 py-2">State Machine</td><td className="px-4 py-2">The full workflow definition containing all states and transitions.</td></tr>
      <tr><td className="px-4 py-2">Task State</td><td className="px-4 py-2">Runs work via Lambda, ECS, or other service integrations.</td></tr>
      <tr><td className="px-4 py-2">Choice State</td><td className="px-4 py-2">Implements branching logic.</td></tr>
      <tr><td className="px-4 py-2">Parallel State</td><td className="px-4 py-2">Executes multiple branches concurrently.</td></tr>
      <tr><td className="px-4 py-2">Map State</td><td className="px-4 py-2">Iterates through a list of items to run sub-workflows for each.</td></tr>
      <tr><td className="px-4 py-2">Wait / Pass / Succeed / Fail</td><td className="px-4 py-2">Utility states for control flow, waiting, or ending workflows.</td></tr>
      <tr><td className="px-4 py-2">Retry & Catch</td><td className="px-4 py-2">Handle transient failures and define fallback routes.</td></tr>
      <tr><td className="px-4 py-2">ResultPath / Parameters</td><td className="px-4 py-2">Shape and limit data passed between states.</td></tr>
      <tr><td className="px-4 py-2">Task Token</td><td className="px-4 py-2">Callback handle for external processes to resume execution.</td></tr>
      <tr><td className="px-4 py-2">Express vs Standard</td><td className="px-4 py-2">Express = short, high-volume; Standard = long-running, durable.</td></tr>
      <tr><td className="px-4 py-2">Execution ARN</td><td className="px-4 py-2">Unique identifier for a specific workflow run.</td></tr>
      <tr><td className="px-4 py-2">Integration Patterns</td><td className="px-4 py-2">Defines interaction model: request-response, wait-for-callback, etc.</td></tr>
      <tr><td className="px-4 py-2">Service Integrations</td><td className="px-4 py-2">Direct connections to AWS services (e.g., S3, DynamoDB, ECS).</td></tr>
      <tr><td className="px-4 py-2">CloudWatch / X-Ray</td><td className="px-4 py-2">Monitoring and distributed tracing tools for observability.</td></tr>
      <tr><td className="px-4 py-2">Provisioned Concurrency</td><td className="px-4 py-2">Keeps Lambda warm for predictable low latency.</td></tr>
      <tr><td className="px-4 py-2">DLQ (Dead Letter Queue)</td><td className="px-4 py-2">Captures failed events for postmortem analysis.</td></tr>
      <tr><td className="px-4 py-2">Idempotency</td><td className="px-4 py-2">Design principle to ensure retries produce consistent results.</td></tr>
      <tr><td className="px-4 py-2">Audit Trail</td><td className="px-4 py-2">Execution logs serve as traceable records for compliance.</td></tr>
    </tbody>
  </table>
 
</div>


          </article>
        </div>
      </div>
    </div>

    <div className="d-flex align-items-center gap-2 small text-muted border-top pt-3">
                <span>Was this helpful?</span>
                <Link to="/contact" className="btn btn-sm btn-outline-primary">Suggest an edit</Link>
              </div>
    
              <ScrollButton />
        </main>

        {/* RIGHT: Sidebar */}
        <aside className="col-12 col-lg-4 col-xl-3 pt-3 border-start">
          <BlogSideBarRight />
        </aside>
      </div>
    </div>
  );
};

export default LambdaStepFunctionBlog;
